import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Building2, 
  Users, 
  UserCheck, 
  UserX, 
  Coffee, 
  Clock,
  TrendingUp,
  Activity,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Wallet,
  Bell,
  CreditCard,
  Cpu,
  MemoryStick,
  HardDrive,
  RefreshCw
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  RadialBarChart,
  RadialBar
} from 'recharts';
import { useLanguage } from '@/i18n/LanguageContext';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
const CURRENCY_COLORS = {
  'USD': '#10b981',
  'EUR': '#3b82f6', 
  'GBP': '#8b5cf6',
  'PKR': '#f59e0b',
  'INR': '#ef4444',
  'AED': '#06b6d4',
  'SAR': '#ec4899',
  'CAD': '#84cc16',
  'AUD': '#f97316'
};

function DashboardHome({ socket }) {
  const { t, isRTL } = useLanguage();
  const [stats, setStats] = useState(null);
  const [reportsSummary, setReportsSummary] = useState(null);
  const [paymentGraph, setPaymentGraph] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hardware monitoring state
  const [hw, setHw] = useState(null);
  const [hwHistory, setHwHistory] = useState([]); // rolling 20 readings
  const hwIntervalRef = useRef(null);
  
  useEffect(() => {
    fetchStats();
    fetchReportsSummary();
    fetchPaymentGraph();
    fetchRecentPayments();
    
    const handleUpdate = () => {
      fetchStats();
      fetchReportsSummary();
      fetchPaymentGraph();
      fetchRecentPayments();
    };
    
    window.addEventListener('dashboard_update', handleUpdate);
    
    return () => {
      window.removeEventListener('dashboard_update', handleUpdate);
    };
  }, []);

  // Hardware polling — every 5 seconds
  useEffect(() => {
    const fetchHw = async () => {
      try {
        const res = await axios.get(`${API}/superadmin/hardware-stats`, { withCredentials: true });
        const d = res.data;
        setHw(d);
        setHwHistory(prev => {
          const label = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
          const next = [...prev, { t: label, cpu: d.cpu.percent, ram: d.ram.percent, disk: d.disk.percent }];
          return next.slice(-20);
        });
      } catch { /* silent */ }
    };
    fetchHw();
    hwIntervalRef.current = setInterval(fetchHw, 5000);
    return () => clearInterval(hwIntervalRef.current);
  }, []);
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReportsSummary = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/reports/summary`, {
        withCredentials: true
      });
      setReportsSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch reports summary:', error);
    }
  };
  
  const fetchPaymentGraph = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/payment-graph`, {
        params: { days: 30 },
        withCredentials: true
      });
      setPaymentGraph(response.data);
    } catch (error) {
      console.error('Failed to fetch payment graph:', error);
    }
  };
  
  const fetchRecentPayments = async () => {
    try {
      // Get payments from last 7 days
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/superadmin/payment-history`, {
        params: { 
          start_date: startDate,
          end_date: endDate,
          limit: 10
        },
        withCredentials: true
      });
      setRecentPayments(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch recent payments:', error);
    }
  };
  
  // Format currency amount
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  // Get time ago string
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };
  
  // Prepare chart data for multi-currency
  const prepareChartData = () => {
    if (!paymentGraph?.data || !paymentGraph?.currencies) return [];
    
    return paymentGraph.data.map(day => {
      const entry = { date: day.date, full_date: day.full_date };
      paymentGraph.currencies.forEach(currency => {
        entry[currency] = day.by_currency?.[currency]?.amount || 0;
      });
      return entry;
    });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  // Calculate attendance rate for pie chart
  const attendanceData = reportsSummary ? [
    { name: 'Present', value: reportsSummary.summary?.present_today || 0, color: '#10b981' },
    { name: 'Late', value: reportsSummary.summary?.late_today || 0, color: '#f59e0b' },
    { name: 'Absent', value: reportsSummary.summary?.absent_today || 0, color: '#ef4444' },
  ].filter(item => item.value > 0) : [];
  
  const statCards = [
    {
      title: 'Total Companies',
      value: reportsSummary?.summary?.total_companies || stats?.total_companies || 0,
      subtitle: `${reportsSummary?.summary?.active_companies || 0} Active`,
      icon: Building2,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      borderColor: 'border-indigo-200'
    },
    {
      title: 'Total Staff',
      value: reportsSummary?.summary?.total_staff || stats?.total_staff || 0,
      subtitle: `${reportsSummary?.summary?.active_staff || 0} Active`,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-200'
    },
    {
      title: 'Present Today',
      value: reportsSummary?.summary?.present_today || stats?.present_staff || 0,
      subtitle: 'Checked in',
      icon: UserCheck,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200'
    },
    {
      title: 'Absent Today',
      value: reportsSummary?.summary?.absent_today || stats?.absent_staff || 0,
      subtitle: 'Not checked in',
      icon: UserX,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-200'
    },
    {
      title: 'Late Today',
      value: reportsSummary?.summary?.late_today || stats?.late_staff || 0,
      subtitle: 'Arrived late',
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-200'
    },
    {
      title: 'On Break',
      value: stats?.staff_on_break || 0,
      subtitle: 'Currently on break',
      icon: Coffee,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-200'
    },
  ];
  
  return (
    <div className="space-y-6" data-testid="dashboard-home">

      {/* ── Hardware Critical Alert Banner ─────────────────────────────── */}
      {hw && (() => {
        const alerts = [
          hw.cpu.percent >= 90 && { label: 'CPU', value: `${hw.cpu.percent}%`, icon: '🖥️' },
          hw.ram.percent >= 90 && { label: 'RAM', value: `${hw.ram.percent}%  (${hw.ram.used_gb} GB / ${hw.ram.total_gb} GB)`, icon: '🧠' },
          hw.disk.percent >= 90 && { label: 'Hard Drive', value: `${hw.disk.percent}%  (${hw.disk.used_gb} GB / ${hw.disk.total_gb} GB)`, icon: '💾' },
        ].filter(Boolean);

        if (!alerts.length) return null;

        return (
          <div
            className="rounded-xl border-2 border-red-400 bg-red-50 px-5 py-4 flex items-start gap-4 shadow-md animate-pulse"
            data-testid="hardware-critical-alert"
            style={{ animationDuration: '2s' }}
          >
            <div className="flex-shrink-0 mt-0.5">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-red-700 text-base">Server Hardware Critical Alert</p>
              <p className="text-red-600 text-sm mt-0.5">
                The following resources have exceeded <strong>90%</strong> capacity. Immediate action is recommended.
              </p>
              <div className="flex flex-wrap gap-3 mt-3">
                {alerts.map((a, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 bg-red-100 border border-red-300 text-red-800 text-sm font-semibold px-3 py-1.5 rounded-lg"
                  >
                    <span>{a.icon}</span>
                    {a.label}: <span className="text-red-600">{a.value}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-1 text-xs text-red-500 bg-red-100 border border-red-200 px-2 py-1 rounded-full">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-ping inline-block" />
                Live
              </span>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t("nav.dashboard")}</h2>
          <p className="text-gray-600 mt-1">{today}</p>
        </div>
        <div className="flex items-center gap-6">
          {/* Payment Alerts Bell */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="relative p-2" data-testid="payment-alerts-bell">
                <Bell className="w-6 h-6 text-gray-600" />
                {recentPayments.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
                    {recentPayments.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="end">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-indigo-600" />
                  Recent Payments (Last 7 Days)
                </h3>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {recentPayments.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No payments in the last 7 days</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {recentPayments.map((payment) => (
                      <div key={payment.transaction_id} className="p-3 hover:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 text-sm">{payment.company_name}</p>
                            <p className="text-xs text-gray-500">{payment.admin_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-emerald-600">
                              {payment.currency || 'USD'} {formatCurrency(payment.amount)}
                            </p>
                            <p className="text-xs text-gray-400">{getTimeAgo(payment.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={payment.payment_status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                            {payment.payment_status}
                          </Badge>
                          <span className="text-xs text-gray-400 capitalize">{payment.payment_method}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          
          <div className="text-right">
            <p className="text-5xl font-bold text-indigo-600">
              {reportsSummary?.monthly_stats?.avg_attendance_rate || 0}%
            </p>
            <p className="text-sm text-gray-500">Monthly Attendance Rate</p>
          </div>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card) => (
          <Card 
            key={card.title} 
            className={`border-2 ${card.borderColor} hover:shadow-lg transition-shadow`}
            data-testid={`stat-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col items-center text-center">
                <div className={`p-3 rounded-xl ${card.bgColor} mb-2`}>
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
                <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                <p className="text-xs text-gray-500 font-medium">{card.title}</p>
                <p className="text-xs text-gray-400">{card.subtitle}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Signups Chart */}
        <Card className="lg:col-span-2" data-testid="company-signups-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Company Signups (Last 7 Days)
            </CardTitle>
            <CardDescription>New companies registered</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={stats?.company_signups || []}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#4f46e5" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCount)"
                    name="New Companies"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Today's Attendance Pie Chart */}
        <Card data-testid="attendance-pie-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-600" />
              Today's Attendance
            </CardTitle>
            <CardDescription>Status breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {attendanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={attendanceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {attendanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  No attendance data yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Payment History Chart */}
      <Card data-testid="payment-history-chart">
        <CardHeader>
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-600" />
                Payment History (Last 30 Days)
              </CardTitle>
              <CardDescription>Admin balance top-up transactions by currency</CardDescription>
            </div>
            {paymentGraph?.summary && (
              <div className="flex gap-4 text-right flex-wrap">
                {paymentGraph.summary.by_currency && Object.entries(paymentGraph.summary.by_currency).map(([currency, data]) => (
                  <div key={currency} className="px-3 py-2 rounded-lg border" style={{ borderColor: CURRENCY_COLORS[currency] || '#6b7280' }}>
                    <p className="text-lg font-bold" style={{ color: CURRENCY_COLORS[currency] || '#6b7280' }}>
                      {currency} {data.total_amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                    <p className="text-xs text-gray-500">{data.total_transactions} txns</p>
                  </div>
                ))}
                {(!paymentGraph.summary.by_currency || Object.keys(paymentGraph.summary.by_currency).length === 0) && (
                  <div className="px-3 py-2 rounded-lg border border-gray-200">
                    <p className="text-lg font-bold text-gray-400">No data</p>
                    <p className="text-xs text-gray-500">No payments yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {paymentGraph?.data && paymentGraph.currencies && paymentGraph.currencies.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={prepareChartData()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    interval={2}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                    formatter={(value, name) => [`${name} ${formatCurrency(value)}`, '']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value) => <span style={{ color: CURRENCY_COLORS[value] || '#6b7280' }}>{value}</span>}
                  />
                  {paymentGraph.currencies.map((currency, index) => (
                    <Bar
                      key={currency}
                      dataKey={currency}
                      stackId="a"
                      fill={CURRENCY_COLORS[currency] || COLORS[index % COLORS.length]}
                      name={currency}
                      radius={index === paymentGraph.currencies.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <DollarSign className="w-12 h-12 mb-2 opacity-50" />
                <p>No payment data yet</p>
                <p className="text-sm">Payments will appear here when admins add balance</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Company Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Companies by Attendance */}
        <Card data-testid="top-companies-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Top Companies by Attendance
            </CardTitle>
            <CardDescription>Today's attendance rate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(reportsSummary?.company_breakdown || []).slice(0, 5).map((company, index) => (
                <div key={company.company_id} className="flex items-center gap-4">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                    ${index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                      index === 1 ? 'bg-gray-100 text-gray-700' : 
                      index === 2 ? 'bg-orange-100 text-orange-700' : 
                      'bg-indigo-50 text-indigo-600'}
                  `}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{company.company_name}</p>
                    <p className="text-xs text-gray-500">
                      {company.present_today} / {company.total_staff} present
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      company.attendance_rate >= 80 ? 'text-green-600' : 
                      company.attendance_rate >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {company.attendance_rate}%
                    </p>
                  </div>
                </div>
              ))}
              {(!reportsSummary?.company_breakdown || reportsSummary.company_breakdown.length === 0) && (
                <div className="text-center text-gray-400 py-8">
                  No company data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Monthly Overview */}
        <Card data-testid="monthly-overview-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-indigo-600" />
              Monthly Overview
            </CardTitle>
            <CardDescription>{reportsSummary?.monthly_stats?.month || 'Current Month'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-indigo-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-indigo-600">
                  {reportsSummary?.monthly_stats?.total_work_days || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Work Days</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-600">
                  {reportsSummary?.monthly_stats?.total_present || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Total Present</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">
                  {reportsSummary?.monthly_stats?.total_late || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Total Late</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-red-600">
                  {reportsSummary?.monthly_stats?.total_absent || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Total Absent</p>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Average Attendance Rate</span>
                <span className="text-2xl font-bold text-indigo-600">
                  {reportsSummary?.monthly_stats?.avg_attendance_rate || 0}%
                </span>
              </div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-3 rounded-full transition-all"
                  style={{ width: `${reportsSummary?.monthly_stats?.avg_attendance_rate || 0}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

    {/* ── Server Hardware Monitor ─────────────────────────────────────── */}
    <div className="mt-2">      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" />
            Server Hardware Monitor
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Live metrics — auto-refreshes every 5 seconds</p>
        </div>
        {hw && (
          <Badge variant="outline" className="gap-1.5 text-green-700 border-green-300 bg-green-50">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block" />
            Live
          </Badge>
        )}
      </div>

      {/* Gauge cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* CPU */}
        <HwGaugeCard
          icon={<Cpu className="w-5 h-5" />}
          label="CPU Usage"
          percent={hw?.cpu?.percent ?? 0}
          color="#6366f1"
          detail={hw ? [
            { label: 'Physical Cores', value: hw.cpu.cores },
            { label: 'Logical Cores', value: hw.cpu.logical_cores },
            hw.cpu.freq_mhz ? { label: 'Frequency', value: `${hw.cpu.freq_mhz} MHz` } : null,
          ].filter(Boolean) : []}
          thresholds={{ warn: 70, crit: 90 }}
        />

        {/* RAM */}
        <HwGaugeCard
          icon={<MemoryStick className="w-5 h-5" />}
          label="RAM Usage"
          percent={hw?.ram?.percent ?? 0}
          color="#10b981"
          detail={hw ? [
            { label: 'Used', value: `${hw.ram.used_gb} GB` },
            { label: 'Total', value: `${hw.ram.total_gb} GB` },
            { label: 'Available', value: `${hw.ram.available_gb} GB` },
          ] : []}
          thresholds={{ warn: 75, crit: 90 }}
        />

        {/* Disk */}
        <HwGaugeCard
          icon={<HardDrive className="w-5 h-5" />}
          label="Hard Drive"
          percent={hw?.disk?.percent ?? 0}
          color="#f59e0b"
          detail={hw ? [
            { label: 'Used', value: `${hw.disk.used_gb} GB` },
            { label: 'Total', value: `${hw.disk.total_gb} GB` },
            { label: 'Free', value: `${hw.disk.free_gb} GB` },
          ] : []}
          thresholds={{ warn: 80, crit: 95 }}
        />
      </div>

      {/* History sparkline */}
      {hwHistory.length > 1 && (
        <Card className="mt-6 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-400" />
              Usage History (last {hwHistory.length} readings)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={hwHistory} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gCpu" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gRam" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gDisk" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend iconType="circle" iconSize={8} />
                <Area type="monotone" dataKey="cpu" name="CPU" stroke="#6366f1" fill="url(#gCpu)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="ram" name="RAM" stroke="#10b981" fill="url(#gRam)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="disk" name="Disk" stroke="#f59e0b" fill="url(#gDisk)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  </div>
  );
}

/* ── Gauge card sub-component ─────────────────────────────────────────── */
function HwGaugeCard({ icon, label, percent, color, detail, thresholds }) {
  const status = percent >= thresholds.crit ? 'critical' : percent >= thresholds.warn ? 'warning' : 'normal';
  const statusColor = status === 'critical' ? '#ef4444' : status === 'warning' ? '#f59e0b' : color;
  const statusBg = status === 'critical' ? 'bg-red-50 border-red-200' : status === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200';
  const statusText = status === 'critical' ? 'text-red-600' : status === 'warning' ? 'text-amber-600' : 'text-gray-700';

  const gaugeData = [
    { name: label, value: percent, fill: statusColor },
    { name: 'free', value: 100 - percent, fill: '#f3f4f6' },
  ];

  return (
    <Card className={`shadow-sm border ${statusBg}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className={`flex items-center gap-2 font-semibold ${statusText}`}>
            {icon}
            {label}
          </div>
          {status !== 'normal' && (
            <Badge variant="outline" className={status === 'critical' ? 'text-red-600 border-red-300 bg-red-50 text-xs' : 'text-amber-600 border-amber-300 bg-amber-50 text-xs'}>
              {status === 'critical' ? 'Critical' : 'High'}
            </Badge>
          )}
        </div>

        {/* Radial gauge */}
        <div className="flex items-center justify-center">
          <div className="relative">
            <RadialBarChart width={160} height={160} innerRadius={52} outerRadius={72}
              data={gaugeData} startAngle={210} endAngle={-30} barSize={18}>
              <RadialBar dataKey="value" cornerRadius={8} />
            </RadialBarChart>
            {/* Centre text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-extrabold" style={{ color: statusColor }}>{percent}%</span>
            </div>
          </div>
        </div>

        {/* Detail rows */}
        <div className="mt-2 space-y-1.5 border-t pt-3">
          {detail.map((d, i) => (
            <div key={i} className="flex justify-between text-xs text-gray-600">
              <span className="text-gray-400">{d.label}</span>
              <span className="font-medium">{d.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default DashboardHome;
